import { useState, useEffect, useRef } from 'react'

import csrfHeaders from 'src/utils/csrfHeaders'

import paymentMethodLabel from './paymentMethodLabel'
import { genericError } from './useRecord'

const subscriptionIntervals = {
  monthly:   { interval: 'month', interval_count: 1 },
  quarterly: { interval: 'month', interval_count: 3 },
  annual:    { interval: 'year',  interval_count: 1 },
}

// Adapted from donate_v6/fiat/usePaypal. v6 had an `init()` that toggled a
// separate Summary panel; v7's Payment step *is* the summary, so the SDK
// button mounts directly. Plan creation moves into an effect keyed on
// subscription/total/currency so the button itself can stay mounted across
// tip / cover-costs changes. The draft is saved when the donor clicks the
// button, before any PayPal call (v6 saves it on Continue). saveDraft() is a
// new function each render that reads that render's donation, so a caller
// holding on to it must reach it through a ref (as PaypalButton does) or it
// saves stale amounts. process() reads the saved draft and the latest planId
// via refs.
const usePaypal = ({
  i18n, donation, record, paymentMethod, partnerItem, paypal,
  finished, setSubmitInFlight,
}) => {
  const [ planId, setPlanId ] = useState(null)
  const [ idToken, setIdToken ] = useState(null)
  const [ errors, setErrors ] = useState(null)
  const cachedPlans = useRef({})

  // Drop the App-level overlay and surface the inline alert on the
  // PayPal step. Callers pass an already-shaped { base: [...] } —
  // saveDraft normalizes its own controller's response, and SDK
  // failure paths pass genericError(i18n) directly.
  const fail = errors => {
    setSubmitInFlight(false)
    setErrors(errors)
  }

  const subscription = donation.state.subscription
  const isSubscription = subscription !== 'one_time'

  const planIdRef = useRef(planId)
  planIdRef.current = planId

  useEffect(() => {
    // No paypal config when the form doesn't offer PayPal (e.g. Blackbaud).
    if (!paypal || !isSubscription) {
      setPlanId(null)
      setIdToken(null)
      return
    }

    const planKey = JSON.stringify({
      ...subscriptionIntervals[subscription],
      amount: donation.total,
      currency: donation.currency.iso_code,
      descriptor: 'Pledge donation subscription',
    })

    const cached = cachedPlans.current[planKey]
    if (cached) {
      setPlanId(cached.id)
      setIdToken(cached.idToken)
      return
    }

    // Cache miss — a fresh plan must be fetched for the new
    // amount/tip/frequency/currency. Clear the previous plan first so
    // the button disarms (ready → !!planId → false) while the request
    // is in flight AND stays disarmed if it fails, instead of remaining
    // clickable and attaching the order to the previous plan's stale
    // terms.
    setPlanId(null)
    setIdToken(null)

    let cancelled = false
    fetch(paypal.plan_path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: planKey,
    })
      .then(response => response.json().then(json => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok) {
          console.warn(json)
          throw new Error('Validation or server error')
        }
        if (cancelled) return
        cachedPlans.current[planKey] = { id: json.id, idToken: json.id_token }
        setPlanId(json.id)
        setIdToken(json.id_token)
      })
      .catch(error => {
        console.error('Error creating PayPal plan:', error)
        if (!cancelled) fail(genericError(i18n))
      })

    return () => { cancelled = true }
  }, [ isSubscription, subscription, donation.total, donation.currency.iso_code ])

  const draft = useRef()

  // Called on the button click, before any PayPal call, so the PayPal order
  // is created for the draft (PaypalButton sends the token it returns) and
  // can be traced back to it. Null when the save failed, with the error on
  // the PayPal step.
  const saveDraft = async () => {
    const saved = await record.saveDraft({
      order: {
        payment_method_used: paymentMethodLabel(paymentMethod, i18n),
        payment_source: paymentMethod,
      },
    })

    if (saved.errors) {
      fail(saved.errors)
      return null
    }

    draft.current = saved
    return saved.token
  }

  // Called as soon as PayPal hands back a recurring gift's approval, since
  // PaypalButton then saves the vault token and charges the first month
  // (createOrder) before process() runs. Covering the panel from there keeps
  // the donor from starting a second attempt while the first is charging.
  const startCharge = () => setSubmitInFlight(true)

  const process = async ({ orderID, paymentToken, customerId }) => {
    // PayPal SDK has already authenticated the donor by the time process()
    // runs (this fires on onApprove). The processor POST can take a few
    // seconds, especially for subscriptions; cover the panel with the
    // App-level overlay so the donor can't double-submit or wonder whether
    // the PayPal flow is still progressing.
    setSubmitInFlight(true)

    const { token, serializedDonation } = draft.current

    const params = { order_id: token, processing_id: orderID }

    if (paymentToken) {
      params.subscription = {
        plan_id: planIdRef.current,
        customer_id: customerId,
        payment_token: paymentToken,
      }
    }

    try {
      const response = await fetch(paypal.process_path, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify(params),
      })
      if (!response.ok) {
        const json = await response.json()
        console.warn(json)
        throw new Error('Validation or server error')
      }
      const { state, ...rest } = donation
      finished({
        ...state,
        ...rest,
        ...serializedDonation,
        token,
        partnerItem,
      })
    } catch (error) {
      console.error('Error processing PayPal payment:', error)
      fail(genericError(i18n))
    }
  }

  return {
    saveDraft,
    startCharge,
    process,
    fail,
    errors,
    setErrors,
    idToken,
    // Subscription mode waits for planId before mounting the SDK button;
    // one-time can mount immediately.
    ready: !isSubscription || !!planId,
  }
}

export default usePaypal
